import type {
  BoardPoint,
  BoardSize,
  FoxKifuClassroomResearchBody,
  StoneColor,
} from '@wx-weiqi/contracts';

export type ClassroomPlacementMode = 'ALTERNATE' | 'BLACK_ONLY' | 'WHITE_ONLY';

export interface ClassroomPlacementResult {
  readonly stones: readonly (StoneColor | null)[];
  readonly color: StoneColor;
  readonly countsAsMove: boolean;
  readonly nextAlternateColor: StoneColor;
}

export interface ClassroomRuleHistory {
  readonly request: FoxKifuClassroomResearchBody;
  readonly nextColor: StoneColor;
}

export interface ClassroomFrame {
  readonly moveNumber: number;
  readonly stones: readonly (StoneColor | null)[];
  readonly lastMove: BoardPoint | null;
  readonly color: StoneColor | null;
  readonly ruleHistory: ClassroomRuleHistory | null;
}

export function classroomRecordHistory(
  boardSize: BoardSize,
  initialStones: readonly (StoneColor | null)[],
  initialPlayer: StoneColor,
  history: NonNullable<FoxKifuClassroomResearchBody['history']>,
): ClassroomRuleHistory {
  const last = history.at(-1);
  return {
    nextColor: last === undefined ? initialPlayer : nextClassroomAlternateColor(last.color),
    request: {
      boardSize,
      stones: [...initialStones],
      currentPlayer: initialPlayer,
      history,
      moves: [],
    },
  };
}

/** Retain the replay prefix, not just the current stones: the server needs it to enforce ko. */
export function classroomRulePosition(
  stones: readonly (StoneColor | null)[],
  boardSize: BoardSize,
  currentPlayer: StoneColor,
  history: ClassroomRuleHistory | null,
): FoxKifuClassroomResearchBody {
  if (
    history !== null &&
    history.nextColor === currentPlayer &&
    history.request.boardSize === boardSize
  ) {
    return history.request;
  }
  return { boardSize, currentPlayer, stones: [...stones], moves: [] };
}

export function appendClassroomRuleMoves(
  position: FoxKifuClassroomResearchBody,
  moves: readonly BoardPoint[],
): FoxKifuClassroomResearchBody {
  return { ...position, moves: [...position.moves, ...moves] };
}

export function classroomPlacementColor(
  mode: ClassroomPlacementMode,
  alternateColor: StoneColor,
): StoneColor {
  if (mode === 'BLACK_ONLY') return 'BLACK';
  if (mode === 'WHITE_ONLY') return 'WHITE';
  return alternateColor;
}

export function nextClassroomAlternateColor(color: StoneColor): StoneColor {
  return color === 'BLACK' ? 'WHITE' : 'BLACK';
}

/** Right-click setup is a one-off placement; it never switches mode, counts a move, or removes a stone. */
export function applyClassroomSecondaryPlacement(
  stones: readonly (StoneColor | null)[],
  size: BoardSize,
  point: BoardPoint,
  mode: ClassroomPlacementMode,
  alternateColor: StoneColor,
): ClassroomPlacementResult | null {
  if (mode === 'ALTERNATE') return null;
  const oppositeMode = mode === 'BLACK_ONLY' ? 'WHITE_ONLY' : 'BLACK_ONLY';
  const result = applyClassroomPlacement(stones, size, point, oppositeMode, alternateColor);
  if (result === null || result.stones[point.y * size + point.x] === null) return null;
  return result;
}

export function classroomAlternateColorAtOffset(
  startingColor: StoneColor,
  moveOffset: number,
): StoneColor {
  // A navigation cursor can precede an explicitly selected starting point.
  // Backward steps alternate too; clamping them to zero repeats the anchor color.
  const normalizedOffset = Math.trunc(moveOffset);
  return normalizedOffset % 2 === 0 ? startingColor : nextClassroomAlternateColor(startingColor);
}

/** An explicit side choice applies at its cursor; replayed frames retain their own next side. */
export function classroomAlternateColorAtFrame(
  startingColor: StoneColor,
  moveOffset: number,
  history: ClassroomRuleHistory | null,
): StoneColor {
  if (moveOffset === 0) return startingColor;
  return history?.nextColor ?? classroomAlternateColorAtOffset(startingColor, moveOffset);
}

/**
 * Unnumbered setup editing only. Alternating moves must use the backend rule endpoint.
 * Never emulate captures or legality here; authoritative rules remain in packages/go-core.
 */
export function applyClassroomPlacement(
  stones: readonly (StoneColor | null)[],
  size: BoardSize,
  point: BoardPoint,
  mode: ClassroomPlacementMode,
  alternateColor: StoneColor,
): ClassroomPlacementResult | null {
  if (
    mode === 'ALTERNATE' ||
    stones.length !== size * size ||
    !Number.isInteger(point.x) ||
    !Number.isInteger(point.y) ||
    point.x < 0 ||
    point.y < 0 ||
    point.x >= size ||
    point.y >= size
  ) {
    return null;
  }

  const color = classroomPlacementColor(mode, alternateColor);
  const index = point.y * size + point.x;
  const current = stones[index] ?? null;
  const next = [...stones];
  next[index] = current === color ? null : color;
  return Object.freeze({
    stones: Object.freeze(next),
    color,
    countsAsMove: false,
    nextAlternateColor: alternateColor,
  });
}
